import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { Clock, Share2, ExternalLink, Loader2 } from 'lucide-react';
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer } from 'recharts';
import toast from 'react-hot-toast';
import { useI18n } from '../i18n/useI18n';

interface NewsArticle {
  id: string;
  title: string;
  content: string;
  summary: string;
  deepAnalysis: string;
  category: string;
  imageUrl: string | null;
  biasScore: number;
  noticiabilityTotal: number;
  scoreJson: { impact: number; reach: number; duration: number };
  sourceName: string;
  sourceUrl: string;
  publishedAt: string;
}

const ArticlePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { t, locale } = useI18n();
  const [showRegistration, setShowRegistration] = useState(false);
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => { window.scrollTo(0, 0); }, [id]);

  const { data: articleData, isLoading, error } = useQuery({
    queryKey: ['article', id],
    queryFn: async () => {
      const response = await axios.get(`/api/v1/news/${id}`);
      return response.data;
    },
    enabled: !!id
  });

  // Fetch translation when locale is Spanish
  const { data: translationData, isLoading: isTranslating } = useQuery({
    queryKey: ['translation', id, locale],
    queryFn: async () => {
      const response = await axios.get(`/api/v1/news/${id}/translate/es`);
      return response.data;
    },
    enabled: !!id && locale === 'es' && !!articleData?.data,
    staleTime: Infinity, // Translations don't change
    retry: 1,
  });

  const handleRegistration = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      localStorage.setItem('userEmail', userEmail);
      try { await axios.post('/api/v1/auth/subscribe', { email: userEmail }); } catch {}
      setShowRegistration(false);
      toast.success(t('register.success'));
    } catch {
      toast.error('Error');
    }
  };

  if (error) {
    return (
      <div className="container-news py-12">
        <div className="text-center">
          <h2 className="font-playfair text-2xl font-bold text-primary-900 mb-4">{t('article.notFound')}</h2>
          <p className="text-primary-600">{t('article.notFoundDesc')}</p>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="container-news py-12">
        <div className="max-w-4xl mx-auto">
          <div className="loading-skeleton h-8 mb-4"></div>
          <div className="loading-skeleton h-64 mb-6"></div>
          <div className="loading-skeleton h-4 mb-2"></div>
          <div className="loading-skeleton h-4 mb-2"></div>
          <div className="loading-skeleton h-4 w-3/4"></div>
        </div>
      </div>
    );
  }

  const article: NewsArticle = articleData?.data;
  if (!article) return <div>{t('article.notFound')}</div>;

  // Use translated content if available and locale is Spanish
  const tr = locale === 'es' && translationData?.data;
  const displayTitle = tr ? tr.title : article.title;
  const displaySummary = tr ? tr.summary : article.summary;
  const displayAnalysis = tr ? tr.deepAnalysis : article.deepAnalysis;

  const radarData = [
    { subject: t('article.impact'), value: article.scoreJson.impact, fullMark: 10 },
    { subject: t('article.globalReach'), value: article.scoreJson.reach, fullMark: 10 },
    { subject: t('article.duration'), value: article.scoreJson.duration, fullMark: 10 }
  ];

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(locale === 'es' ? 'es-ES' : 'en-US', {
      year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit'
    });
  };

  const renderAnalysis = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('## ')) return <h2 key={i} className="font-playfair text-2xl font-bold text-primary-900 mt-8 mb-4">{line.replace('## ', '')}</h2>;
      if (line.startsWith('### ')) return <h3 key={i} className="font-semibold text-lg text-primary-800 mt-6 mb-3">{line.replace('### ', '')}</h3>;
      if (line.startsWith('- ')) return <li key={i} className="ml-4 mb-2 text-primary-700">{line.replace('- ', '')}</li>;
      if (line.match(/^\d+\.\s/)) return <li key={i} className="ml-4 mb-2 text-primary-700 list-decimal">{line.replace(/^\d+\.\s/, '')}</li>;
      if (line.trim() === '') return <div key={i} className="h-2"></div>;
      const parts = line.split(/(\*\*.*?\*\*)/g);
      return (
        <p key={i} className="text-primary-700 leading-relaxed mb-2">
          {parts.map((part, j) => {
            if (part.startsWith('**') && part.endsWith('**')) return <strong key={j} className="text-primary-900">{part.slice(2, -2)}</strong>;
            return part;
          })}
        </p>
      );
    });
  };

  return (
    <div className="bg-white">
      <article className="container-news py-12">
        <div className="max-w-4xl mx-auto">
          {/* Meta */}
          <div className="flex items-center justify-between mb-6 text-sm text-primary-600">
            <div className="flex items-center space-x-4">
              <span className="category-badge">{article.category}</span>
              <span className="flex items-center">
                <Clock className="h-4 w-4 mr-1" />
                {formatDate(article.publishedAt)}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              <button className="flex items-center space-x-1 text-primary-600 hover:text-primary-900">
                <Share2 className="h-4 w-4" /><span>{t('article.share')}</span>
              </button>
            </div>
          </div>

          {/* Title */}
          <h1 className="font-playfair text-4xl md:text-5xl font-bold text-primary-900 leading-tight mb-6">
            {displayTitle}
            {locale === 'es' && isTranslating && (
              <Loader2 className="inline-block ml-3 h-6 w-6 animate-spin text-primary-400" />
            )}
          </h1>

          {/* Image */}
          {article.imageUrl && (
            <div className="mb-8">
              <img src={article.imageUrl} alt={displayTitle} className="w-full h-64 md:h-80 object-cover newspaper-border" />
            </div>
          )}

          {/* Neutral Summary */}
          <div className="bg-primary-50 newspaper-border p-6 mb-8">
            <h2 className="font-playfair text-xl font-bold text-primary-900 mb-4">{t('article.neutralSummary')}</h2>
            <p className="text-lg text-primary-800 leading-relaxed">{displaySummary}</p>
            <p className="text-xs text-primary-400 mt-4 italic">{t('article.synthesized')}</p>
          </div>

          {/* Deep Analysis */}
          <div className="mb-8">
            {locale === 'es' && isTranslating && (
              <div className="flex items-center space-x-2 text-primary-500 mb-4">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">Traduciendo análisis...</span>
              </div>
            )}

            <div className="prose prose-lg max-w-none">
              {renderAnalysis(displayAnalysis)}
            </div>

            <div className="mt-8 pt-4 border-t border-primary-200">
              <p className="text-sm text-primary-500">
                <strong>{t('article.sourcesConsulted')}</strong> {article.sourceName}
                {article.sourceUrl && (
                  <> — <a href={article.sourceUrl} target="_blank" rel="noopener noreferrer" className="text-accent-600 hover:underline inline-flex items-center">
                    {t('article.readOriginal')} <ExternalLink className="h-3 w-3 ml-1" />
                  </a></>
                )}
              </p>
              <p className="text-xs text-primary-400 mt-1 italic">{t('article.originalContent')}</p>
            </div>
          </div>
        </div>
      </article>

      {/* Story Metrics */}
      <section className="bg-primary-50 newspaper-divider">
        <div className="container-news py-12">
          <div className="max-w-4xl mx-auto">
            <h2 className="headline-section mb-8">{t('article.storyMetrics')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              
              {/* Radar Chart */}
              <div className="bg-white newspaper-border p-6">
                <h4 className="font-playfair text-lg font-bold text-primary-900 mb-4">{t('article.impactAnalysis')}</h4>
                <ResponsiveContainer width="100%" height={200}>
                  <RadarChart data={radarData}>
                    <PolarGrid />
                    <PolarAngleAxis dataKey="subject" />
                    <PolarRadiusAxis angle={30} domain={[0, 10]} />
                    <Radar name="Score" dataKey="value" stroke="#f59e55" fill="#f59e55" fillOpacity={0.3} />
                  </RadarChart>
                </ResponsiveContainer>
                <div className="space-y-2 mt-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-primary-600">{t('article.impact')}:</span>
                    <span className="font-bold">{article.scoreJson.impact}/10</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">{t('article.globalReach')}:</span>
                    <span className="font-bold">{article.scoreJson.reach}/10</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-primary-600">{t('article.duration')}:</span>
                    <span className="font-bold">{article.scoreJson.duration}/10</span>
                  </div>
                  <div className="border-t pt-2">
                    <div className="flex justify-between">
                      <span className="font-semibold text-primary-900">{t('article.total')}:</span>
                      <span className="font-bold text-lg text-accent-600">{article.noticiabilityTotal.toFixed(0)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Sources */}
              <div className="bg-white newspaper-border p-6">
                <h4 className="font-playfair text-lg font-bold text-primary-900 mb-4">{t('article.sourcesConsulted')}</h4>
                <div className="space-y-3">
                  {article.sourceName?.split(' | ').map((source: string, idx: number) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <div className="w-2 h-2 rounded-full bg-accent-500"></div>
                      <span className="text-sm text-primary-700">{source}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* About */}
              <div className="bg-white newspaper-border p-6">
                <h4 className="font-playfair text-lg font-bold text-primary-900 mb-4">{t('article.aboutAnalysis')}</h4>
                <div className="space-y-3 text-sm text-primary-700">
                  <p>{t('article.aboutDesc1')}</p>
                  <p>{t('article.aboutDesc2')}</p>
                  <p>{t('article.aboutDesc3')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Registration Modal */}
      {showRegistration && (
        <div className="modal-overlay" onClick={() => setShowRegistration(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-playfair text-xl font-bold text-primary-900 mb-4">{t('register.title')}</h3>
            <p className="text-primary-700 mb-6">{t('register.desc')}</p>
            <form onSubmit={handleRegistration} className="space-y-4">
              <input type="email" value={userEmail} onChange={(e) => setUserEmail(e.target.value)} placeholder={t('register.placeholder')} className="form-input" required />
              <div className="flex space-x-3">
                <button type="submit" className="btn-primary flex-1">{t('register.submit')}</button>
                <button type="button" onClick={() => setShowRegistration(false)} className="btn-secondary">{t('register.cancel')}</button>
              </div>
              <p className="text-xs text-primary-500 text-center">{t('register.privacy')}</p>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArticlePage;